import React from 'react'
import TextField from '@mui/material/TextField'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'

export interface OptionType {
    label: string
    value: string | number
}

interface AutocompleteSelectProps {
    /** Current selected primitive value (id). Use '' for empty */
    value: string | number | ''
    /** When user selects a new value, primitive id (or '') is emitted */
    onChange: (value: string | number | '') => void
    /** List of selectable options */
    options: OptionType[]
    /** Mark control in error state */
    error?: boolean
    /** Pass-through props */
    disabled?: boolean
    fullWidth?: boolean
}

/**
 * Shared MUI Autocomplete implementation with unified styling & accent-insensitive substring filtering.
 * Use to replace plain <TextField select> dropdowns across components.
 */
const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
    value,
    onChange,
    options,
    error = false,
    disabled = false,
    fullWidth = true,
}) => {
    const filterOptions = createFilterOptions<OptionType>({
        ignoreAccents: true,
        matchFrom: 'any',
        stringify: (option) => option.label,
    })

    // Map primitive value back to the option object expected by Autocomplete
    const selectedOption = options.find((o) => String(o.value) === String(value)) || null

    return (
        <Autocomplete
            slotProps={{
                popper: {
                    sx: {
                        '& .MuiAutocomplete-listbox': {
                            p: 0,
                            '& .MuiAutocomplete-option': {
                                fontSize: '0.8125rem',
                                minHeight: 'auto',
                                py: '4px',
                                px: '8px',
                            },
                        },
                    },
                },
            }}

            options={options}
            value={selectedOption}
            filterOptions={filterOptions}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            getOptionLabel={(opt) => opt.label}
            onChange={(_, newVal) => onChange(newVal ? newVal.value : '')}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    margin="none"
                    error={error}
                    sx={{
                        '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 },
                        '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' },
                    }}
                />
            )}
            fullWidth={fullWidth}
        />
    )
}

export default AutocompleteSelect
